import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import {
  AlertCircle,
  ArrowRight,
  BookOpen,
  FolderOpen,
  GraduationCap,
  LayoutGrid,
  PlayCircle,
  Store,
  Truck,
  Video,
  Youtube,
} from "lucide-react"
import Navbar from "../../components/LandPage/Navbar"
import Footer from "../../components/LandPage/Footer"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import { SEO } from "../../seo/Seo"
import { softwareSchema, useOrganizationSchema } from "../../seo/schema"
import { PATH_MAP } from "../../configuration/PathMap"
import { ETutorialAudienceType } from "../../Enum/ETutorialAudienceType"
import { TutorialEntity } from "../../Objects/Tutorial/TutorialEntity"
import { useGetPublicTutorial } from "../../hooks/Tutorial/useGetPublicTutorial"
import {
  PublicAnimatedBackground,
  publicAnimatedBackgroundShellClassName,
} from "../../components/PublicAnimatedBackground/PublicAnimatedBackground"
import { extractYoutubeId } from "../../Objects/Generics/Helps"

type AudienceKey = "restaurant" | "courier"

type TutorialGroup = {
  category: string
  items: TutorialEntity[]
}

const groupTutorials = (items: TutorialEntity[], uncategorizedLabel: string): TutorialGroup[] => {
  const grouped = new Map<string, TutorialEntity[]>()

  items.forEach((item) => {
    const key = item.category.trim() || uncategorizedLabel
    const current = grouped.get(key) ?? []

    current.push(item)
    grouped.set(key, current)
  })

  return Array.from(grouped.entries()).map(([category, tutorials]) => ({
    category,
    items: tutorials,
  }))
}

const PublicTutorialsPage: React.FC = () => {
  const organizationSchema = useOrganizationSchema()

  const navigate = useNavigate()

  const { t } = useTranslation("landpage")

  const [activeAudience, setActiveAudience] = useState<AudienceKey>("restaurant")

  const [restaurantTutorials, setRestaurantTutorials] = useState<TutorialEntity[]>([])

  const [courierTutorials, setCourierTutorials] = useState<TutorialEntity[]>([])

  const [isLoading, setIsLoading] = useState(true)

  const [hasError, setHasError] = useState(false)

  const { handleGetPublicTutorialByAudience } = useGetPublicTutorial()

  const audienceConfig: Record<AudienceKey, { title: string; subtitle: string; icon: typeof Store }> = {
    restaurant: {
      title: t("public_tutorials.audiences.restaurant.title"),
      subtitle: t("public_tutorials.audiences.restaurant.subtitle"),
      icon: Store,
    },
    courier: {
      title: t("public_tutorials.audiences.courier.title"),
      subtitle: t("public_tutorials.audiences.courier.subtitle"),
      icon: Truck,
    },
  }

  useEffect(() => {
    const loadTutorials = async () => {
      setIsLoading(true)
      setHasError(false)

      try {
        const [restaurantList, courierList] = await Promise.all([
          handleGetPublicTutorialByAudience(ETutorialAudienceType.Restaurant),
          handleGetPublicTutorialByAudience(ETutorialAudienceType.Courier),
        ])

        setRestaurantTutorials(restaurantList)
        setCourierTutorials(courierList)
      } catch {
        setHasError(true)
      } finally {
        setIsLoading(false)
      }
    }

    loadTutorials()
  }, [handleGetPublicTutorialByAudience])

  const uncategorizedLabel = t("public_tutorials.uncategorized", "Sem categoria")

  const groupedRestaurantTutorials = useMemo(
    () => groupTutorials(restaurantTutorials, uncategorizedLabel),
    [restaurantTutorials, uncategorizedLabel],
  )
  const groupedCourierTutorials = useMemo(
    () => groupTutorials(courierTutorials, uncategorizedLabel),
    [courierTutorials, uncategorizedLabel],
  )

  const currentGroups = activeAudience === "restaurant" ? groupedRestaurantTutorials : groupedCourierTutorials

  const currentTotal = activeAudience === "restaurant" ? restaurantTutorials.length : courierTutorials.length

  return (
    <div
      className={`${publicAnimatedBackgroundShellClassName} h-dvh bg-white relative z-0 overflow-x-hidden overflow-y-scroll scrollbar-none`}
    >
      <PublicAnimatedBackground />

      <SEO
        title={t("public_tutorials.seo_title")}
        description={t("public_tutorials.seo_description")}
        path="/tutoriais"
        structuredData={[organizationSchema, softwareSchema]}
      />

      <Navbar />

      <main>
        <section className="pt-28 pb-14 bg-gradient-to-b bg-white">
          <div className="max-w-[1280px] mx-auto px-6">
            <div className="grid gap-8 lg:grid-cols-[1.2fr_0.8fr] lg:items-center">
              <div>
                <Animate type={AnimationTypes.fadeInTraslate} duration="1.1s">
                  <span className="inline-flex items-center gap-2 rounded-full bg-red-100 px-3 py-1 text-xs font-extrabold uppercase tracking-wide text-red-600">
                    <GraduationCap size={14} /> {t("public_tutorials.hero_badge")}
                  </span>
                </Animate>

                <Animate type={AnimationTypes.fadeInTraslate} duration="1.3s">
                  <h1 className="mt-4 max-w-[880px] text-4xl font-black leading-tight text-slate-900 lg:text-6xl">
                    {t("public_tutorials.hero_title")}
                  </h1>
                </Animate>

                <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                  <p className="mt-4 max-w-[760px] text-base text-slate-600 lg:text-lg">
                    {t("public_tutorials.hero_description")}
                  </p>
                </Animate>

                <Animate type={AnimationTypes.slideRightIn} duration="1.8s">
                  <div className="mt-8 flex flex-wrap gap-3">
                    <button
                      onClick={() => navigate(PATH_MAP.ROOT_PAGE)}
                      className="cursor-pointer rounded-xl bg-gradient-to-r from-red-600 to-orange-500 px-6 py-3 font-bold text-white transition-all duration-300 hover:scale-105"
                      data-interaction-description-key="interactionDescriptions:pages.landPage.publicTutorialsPage.button.navigatePATHMAPROOTPAGE"
                    >
                      {t("public_tutorials.back_to_landing")}
                    </button>

                    <button
                      onClick={() => navigate(PATH_MAP.ROOT_SIGN_UP)}
                      className="cursor-pointer rounded-xl border border-slate-200 px-6 py-3 font-bold text-slate-700 transition-all duration-300 hover:scale-105"
                      data-interaction-description-key="interactionDescriptions:pages.landPage.publicTutorialsPage.button.navigatePATHMAPROOTSIGNUP"
                    >
                      {t("public_tutorials.try_now")}
                    </button>
                  </div>
                </Animate>
              </div>

              <Animate type={AnimationTypes.fadeInTraslate} duration="1.7s">
                <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-1">
                  <div className="rounded-3xl border border-orange-100 bg-white p-5 shadow-sm">
                    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-orange-100 text-orange-600">
                      <LayoutGrid size={20} />
                    </div>

                    <p className="mt-4 text-sm font-black uppercase tracking-wide text-orange-600">
                      {t("public_tutorials.cards.categories_title")}
                    </p>

                    <p className="mt-2 text-sm text-slate-600">{t("public_tutorials.cards.categories_description")}</p>
                  </div>

                  <div className="rounded-3xl border border-red-100 bg-white p-5 shadow-sm">
                    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-red-100 text-red-600">
                      <Video size={20} />
                    </div>

                    <p className="mt-4 text-sm font-black uppercase tracking-wide text-red-600">
                      {t("public_tutorials.cards.practical_videos_title")}
                    </p>

                    <p className="mt-2 text-sm text-slate-600">
                      {t("public_tutorials.cards.practical_videos_description")}
                    </p>
                  </div>

                  <div className="rounded-3xl border border-emerald-100 bg-white p-5 shadow-sm">
                    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-600">
                      <BookOpen size={20} />
                    </div>

                    <p className="mt-4 text-sm font-black uppercase tracking-wide text-emerald-600">
                      {t("public_tutorials.cards.public_access_title")}
                    </p>

                    <p className="mt-2 text-sm text-slate-600">
                      {t("public_tutorials.cards.public_access_description")}
                    </p>
                  </div>
                </div>
              </Animate>
            </div>
          </div>
        </section>

        <section className="pb-16">
          <div className="max-w-[1280px] mx-auto px-6">
            <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm sm:p-4">
              <div className="flex flex-col gap-3 border-b border-slate-100 pb-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="text-xs font-black uppercase tracking-wide text-orange-600">
                    {t("public_tutorials.journey_badge")}
                  </p>

                  <h2 className="mt-1 text-2xl font-black text-slate-900">{t("public_tutorials.journey_title")}</h2>
                </div>

                <div className="grid gap-2 sm:grid-cols-2">
                  {Object.entries(audienceConfig).map(([key, config]) => {
                    const isActive = activeAudience === key

                    const Icon = config.icon

                    const total = key === "restaurant" ? restaurantTutorials.length : courierTutorials.length

                    return (
                      <button
                        key={key}
                        onClick={() => setActiveAudience(key as AudienceKey)}
                        className={`cursor-pointer rounded-2xl border px-4 py-3 text-left transition-all duration-300 ${
                          isActive
                            ? "border-transparent bg-gradient-to-r from-red-600 to-orange-500 text-white shadow-lg"
                            : "border-slate-200 bg-slate-50 text-slate-700 hover:border-orange-200 hover:bg-white"
                        }`}
                        data-interaction-description-key="interactionDescriptions:pages.landPage.publicTutorialsPage.button.setActiveAudienceKeyAsAudienceKey"
                      >
                        <div className="flex items-start gap-3">
                          <div
                            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl ${
                              isActive ? "bg-white/20" : "bg-white text-orange-600"
                            }`}
                          >
                            <Icon size={18} />
                          </div>

                          <div>
                            <p className="text-sm font-black">{config.title}</p>

                            <p className={`mt-1 text-xs ${isActive ? "text-orange-100" : "text-slate-500"}`}>
                              {config.subtitle}
                            </p>

                            <p className={`mt-2 text-xs font-bold ${isActive ? "text-white" : "text-slate-600"}`}>
                              {total}{" "}
                              {total === 1
                                ? t("public_tutorials.video_count_one")
                                : t("public_tutorials.video_count_other")}
                            </p>
                          </div>
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>

              {isLoading && (
                <div className="grid gap-4 py-8 md:grid-cols-2 xl:grid-cols-3">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <div key={index} className="animate-pulse rounded-3xl border border-slate-200 p-4">
                      <div className="h-5 w-24 rounded bg-slate-200" />

                      <div className="mt-4 aspect-video rounded-2xl bg-slate-200" />

                      <div className="mt-4 h-4 w-3/4 rounded bg-slate-200" />
                    </div>
                  ))}
                </div>
              )}

              {!isLoading && hasError && (
                <div className="flex flex-col items-center justify-center gap-3 px-6 py-12 text-center">
                  <AlertCircle className="text-orange-500" size={28} />

                  <h3 className="text-xl font-black text-slate-900">{t("public_tutorials.load_error_title")}</h3>

                  <p className="max-w-[560px] text-sm text-slate-500">{t("public_tutorials.load_error_description")}</p>
                </div>
              )}

              {!isLoading && !hasError && (
                <>
                  <div className="flex flex-wrap items-center gap-3 px-1 py-5">
                    <span className="inline-flex items-center gap-2 rounded-full bg-orange-50 px-3 py-1 text-xs font-black uppercase tracking-wide text-orange-600">
                      <FolderOpen size={14} /> {t("public_tutorials.category_count", { count: currentGroups.length })}
                    </span>

                    <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600">
                      <PlayCircle size={14} /> {t("public_tutorials.published_count", { count: currentTotal })}
                    </span>
                  </div>

                  {currentGroups.length === 0 ? (
                    <div className="flex flex-col items-center justify-center gap-3 px-6 py-12 text-center">
                      <AlertCircle className="text-orange-500" size={28} />

                      <h3 className="text-xl font-black text-slate-900">{t("public_tutorials.empty_title")}</h3>

                      <p className="max-w-[560px] text-sm text-slate-500">{t("public_tutorials.empty_description")}</p>
                    </div>
                  ) : (
                    <div className="space-y-8">
                      {currentGroups.map((group, index) => (
                        <Animate
                          key={`${activeAudience}-${group.category}`}
                          type={AnimationTypes.fadeInTraslate}
                          duration={`${0.9 + index * 0.15}s`}
                        >
                          <section className="rounded-[1.75rem] border border-orange-100 bg-gradient-to-br from-white to-orange-50/30 p-4 md:p-5">
                            <div className="mb-5 flex flex-wrap items-center justify-between gap-3 border-b border-orange-100 pb-4">
                              <div className="flex items-center gap-3">
                                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-orange-100 text-orange-600">
                                  <FolderOpen size={18} />
                                </div>

                                <div>
                                  <h3 className="text-lg font-black text-slate-900 md:text-xl">{group.category}</h3>

                                  <p className="text-sm text-slate-500">
                                    {group.items.length}{" "}
                                    {group.items.length === 1
                                      ? t("public_tutorials.tutorial_count_one")
                                      : t("public_tutorials.tutorial_count_other")}
                                  </p>
                                </div>
                              </div>
                            </div>

                            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                              {group.items.map((item) => {
                                const videoId = extractYoutubeId(item.youtubeUrl)

                                return (
                                  <article
                                    key={item.id}
                                    className="group overflow-hidden rounded-[1.5rem] border border-slate-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                                  >
                                    <div className="p-4">
                                      <div className="mb-4 flex items-start gap-3">
                                        <div className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-red-50 text-red-500">
                                          <Youtube size={20} />
                                        </div>

                                        <div className="min-w-0 flex-1">
                                          <h4 className="line-clamp-2 text-base font-black text-slate-900">
                                            {item.title}
                                          </h4>

                                          <div className="mt-2 flex items-center gap-2 text-xs font-semibold text-slate-500">
                                            <PlayCircle size={14} />
                                            <span>{t("public_tutorials.tutorial_video")}</span>
                                          </div>
                                        </div>
                                      </div>

                                      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-100 shadow-inner">
                                        <div className="aspect-video">
                                          {videoId ? (
                                            <iframe
                                              className="h-full w-full"
                                              src={`https://www.youtube.com/embed/${videoId}`}
                                              title={item.title}
                                              loading="lazy"
                                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                              referrerPolicy="strict-origin-when-cross-origin"
                                              allowFullScreen
                                            />
                                          ) : (
                                            <div className="flex h-full w-full flex-col items-center justify-center gap-2 px-4 text-center text-slate-500">
                                              <AlertCircle size={18} className="text-orange-500" />

                                              <span className="text-xs font-medium md:text-sm">
                                                {t("public_tutorials.invalid_youtube")}
                                              </span>
                                            </div>
                                          )}
                                        </div>
                                      </div>

                                      {item.youtubeUrl && (
                                        <a
                                          href={item.youtubeUrl}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-red-600 transition-colors hover:text-red-700"
                                          data-interaction-description-key="interactionDescriptions:pages.landPage.publicTutorialsPage.link.watchYoutube"
                                        >
                                          {t("public_tutorials.watch_on_youtube")} <ArrowRight size={16} />
                                        </a>
                                      )}
                                    </div>
                                  </article>
                                )
                              })}
                            </div>
                          </section>
                        </Animate>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  )
}

export default PublicTutorialsPage
